import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useOrgId } from '@/hooks/useOrgContext';
import { emitDeaAudit } from '@/lib/deaAudit';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Search,
  Download,
  UserCheck,
  Package,
  Plus,
  ArrowLeft,
  ClipboardCheck,
  Activity,
  BarChart3,
  List,
  Save,
} from 'lucide-react';
import PtLogo from '@/components/PtLogo';
import { DEAReportModal } from '@/components/DEAReportModal';

// ── Tabs ────────────────────────────────────────────────────────────────────

type Tab = 'ledger' | 'inventory' | 'reconciliation' | 'form222' | 'provider' | 'daily';

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: 'ledger', label: 'Ledger', icon: <List className="w-4 h-4" /> },
  { key: 'inventory', label: 'Current Stock', icon: <Package className="w-4 h-4" /> },
  { key: 'reconciliation', label: 'Reconciliation', icon: <ClipboardCheck className="w-4 h-4" /> },
  { key: 'form222', label: 'Form 222', icon: <FileText className="w-4 h-4" /> },
  { key: 'provider', label: 'Provider Activity', icon: <Activity className="w-4 h-4" /> },
  { key: 'daily', label: 'Daily Usage', icon: <BarChart3 className="w-4 h-4" /> },
];

// ── Interfaces ──────────────────────────────────────────────────────────────

interface DEALogEntry {
  log_id: string;
  transaction_date: string;
  transaction_type: string;
  drug_name: string;
  drug_strength: string;
  dea_schedule: string;
  quantity_received: number | null;
  quantity_administered: number | null;
  quantity_wasted: number | null;
  running_balance: number;
  unit_of_measure: string;
  provider_name: string;
  patient_name: string | null;
  witness_verified: boolean;
  discrepancy_flag: boolean;
  discrepancy_type: string | null;
  discrepancy_resolved: boolean;
  created_by_name: string;
  notes: string | null;
}

interface InventoryRow {
  drug_name: string;
  drug_strength: string;
  drug_form: string;
  dea_schedule: string;
  lot_number: string | null;
  expiration_date: string | null;
  current_quantity: number;
  unit_of_measure: string;
  last_transaction_date: string;
  transaction_count: number;
}

interface DiscrepancyRow {
  log_id: string;
  transaction_date: string;
  drug_name: string;
  dea_schedule: string;
  discrepancy_type: string;
  discrepancy_amount: number;
  unit_of_measure: string;
  discrepancy_notes: string | null;
  discrepancy_resolved: boolean;
  discrepancy_resolved_at: string | null;
  created_by_name: string;
  status: string;
}

interface UnverifiedWasteRow {
  log_id: string;
  transaction_date: string;
  drug_name: string;
  drug_strength: string;
  dea_schedule: string;
  quantity_wasted: number;
  unit_of_measure: string;
  provider_name: string;
  witness_name: string | null;
  patient_name: string | null;
  procedure_name: string | null;
  created_by_name: string;
  created_at: string;
  hours_pending: number;
}

interface Form222Row {
  log_id: string;
  transaction_date: string;
  drug_name: string;
  drug_strength: string;
  drug_form: string;
  ndc_code: string | null;
  lot_number: string | null;
  expiration_date: string | null;
  quantity_received: number;
  unit_of_measure: string;
  supplier_name: string | null;
  invoice_number: string | null;
  form_222_number: string | null;
  received_by_name: string;
  created_at: string;
}

interface ProviderActivityRow {
  provider_id: string | null;
  provider_name: string;
  provider_dea_number: string | null;
  transaction_date: string;
  transaction_type: string;
  drug_name: string;
  dea_schedule: string;
  quantity: number;
  unit_of_measure: string;
  patient_name: string | null;
  procedure_name: string | null;
  witness_verified: boolean;
  discrepancy_flag: boolean;
}

interface DailyUsageRow {
  transaction_date: string;
  drug_name: string;
  dea_schedule: string;
  total_administered: number;
  total_wasted: number;
  administration_count: number;
  waste_count: number;
  discrepancy_count: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const TXN_LABELS: Record<string, string> = {
  receipt: 'Receipt',
  administration: 'Administration',
  waste: 'Waste',
  transfer_in: 'Transfer In',
  transfer_out: 'Transfer Out',
  return: 'Return',
  destruction: 'Destruction',
  theft_loss: 'Theft/Loss',
  inventory_adjustment: 'Adjustment',
  emergency_use: 'Emergency Use',
};

function scheduleColor(s: string) {
  switch (s) {
    case 'I': return 'text-pt-red';
    case 'II': return 'text-pt-red';
    case 'III': return 'text-pt-orange';
    case 'IV': return 'text-pt-amber';
    case 'V': return 'text-pt-green';
    default: return 'text-pt-text-muted';
  }
}

// ── Main Component ──────────────────────────────────────────────────────────

export function DEALogRoute() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const org_id = useOrgId();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>('ledger');
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // ── Ledger state ──────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSchedule, setFilterSchedule] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showDiscrepanciesOnly, setShowDiscrepanciesOnly] = useState(false);
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });

  // ── Stats ─────────────────────────────────────────────────────────────
  const { data: stats } = useQuery({
    queryKey: ['dea-stats'],
    queryFn: async () => {
      const [unverifiedWaste, openDiscrepancies, scheduleIICount, currentInventory] = await Promise.all([
        supabase.from('dea_unverified_waste').select('*', { count: 'exact', head: true }),
        supabase
          .from('dea_controlled_substance_log')
          .select('*', { count: 'exact', head: true })
          .eq('discrepancy_flag', true)
          .eq('discrepancy_resolved', false)
          .is('deleted_at', null),
        supabase
          .from('dea_controlled_substance_log')
          .select('*', { count: 'exact', head: true })
          .eq('dea_schedule', 'II')
          .gte('transaction_date', format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'))
          .is('deleted_at', null),
        supabase.from('dea_current_inventory').select('*', { count: 'exact', head: true }),
      ]);
      return {
        unverifiedWaste: unverifiedWaste.count || 0,
        openDiscrepancies: openDiscrepancies.count || 0,
        scheduleIITransactions: scheduleIICount.count || 0,
        drugsInInventory: currentInventory.count || 0,
      };
    },
  });

  // ── Ledger data ───────────────────────────────────────────────────────
  const { data: logEntries, isLoading: ledgerLoading } = useQuery({
    queryKey: ['dea-log', searchTerm, filterSchedule, filterType, showDiscrepanciesOnly, showUnverifiedOnly, dateRange],
    queryFn: async () => {
      let query = supabase
        .from('dea_controlled_substance_log')
        .select('*')
        .is('deleted_at', null)
        .gte('transaction_date', dateRange.start)
        .lte('transaction_date', dateRange.end)
        .order('transaction_date', { ascending: false });
      if (searchTerm) query = query.or(`drug_name.ilike.%${searchTerm}%,provider_name.ilike.%${searchTerm}%,patient_name.ilike.%${searchTerm}%`);
      if (filterSchedule !== 'all') query = query.eq('dea_schedule', filterSchedule);
      if (filterType !== 'all') query = query.eq('transaction_type', filterType);
      if (showDiscrepanciesOnly) query = query.eq('discrepancy_flag', true);
      if (showUnverifiedOnly) query = query.eq('witness_verified', false).eq('transaction_type', 'waste');
      const { data, error } = await query;
      if (error) throw error;
      return data as DEALogEntry[];
    },
    enabled: tab === 'ledger',
  });

  // ── Inventory data ────────────────────────────────────────────────────
  const { data: inventoryData, isLoading: invLoading } = useQuery({
    queryKey: ['dea-inventory'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dea_current_inventory')
        .select('*')
        .order('drug_name');
      if (error) throw error;
      return data as InventoryRow[];
    },
    enabled: tab === 'inventory',
  });

  // ── Reconciliation data ───────────────────────────────────────────────
  const { data: discrepancies, isLoading: discLoading } = useQuery({
    queryKey: ['dea-discrepancies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dea_discrepancy_report')
        .select('*')
        .order('discrepancy_resolved', { ascending: true });
      if (error) throw error;
      return data as DiscrepancyRow[];
    },
    enabled: tab === 'reconciliation',
  });

  const { data: unverifiedWaste, isLoading: uvLoading } = useQuery({
    queryKey: ['dea-unverified-waste'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dea_unverified_waste')
        .select('*')
        .order('hours_pending', { ascending: false });
      if (error) throw error;
      return data as UnverifiedWasteRow[];
    },
    enabled: tab === 'reconciliation',
  });

  // ── Form 222 data ────────────────────────────────────────────────────
  const { data: form222Data, isLoading: f222Loading } = useQuery({
    queryKey: ['dea-form222'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dea_form222_receipt_log')
        .select('*')
        .order('transaction_date', { ascending: false });
      if (error) throw error;
      return data as Form222Row[];
    },
    enabled: tab === 'form222',
  });

  // ── Provider activity data ───────────────────────────────────────────
  const { data: providerData, isLoading: provLoading } = useQuery({
    queryKey: ['dea-provider-activity'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dea_provider_activity')
        .select('*')
        .gte('transaction_date', format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'))
        .order('transaction_date', { ascending: false });
      if (error) throw error;
      return data as ProviderActivityRow[];
    },
    enabled: tab === 'provider',
  });

  // ── Daily usage data ──────────────────────────────────────────────────
  const { data: dailyData, isLoading: dailyLoading } = useQuery({
    queryKey: ['dea-daily-usage'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dea_daily_usage_summary')
        .select('*')
        .gte('transaction_date', format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'))
        .order('transaction_date', { ascending: false });
      if (error) throw error;
      return data as DailyUsageRow[];
    },
    enabled: tab === 'daily',
  });

  // ── Resolve discrepancy ───────────────────────────────────────────────
  const [resolveModalId, setResolveModalId] = useState<string | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const resolveMutation = useMutation({
    mutationFn: async (logId: string) => {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (!u) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('dea_controlled_substance_log')
        .update({
          discrepancy_resolved: true,
          discrepancy_resolved_at: new Date().toISOString(),
          discrepancy_resolved_by_id: u.id,
          notes: resolveNotes || undefined,
        })
        .eq('log_id', logId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dea-discrepancies'] });
      queryClient.invalidateQueries({ queryKey: ['dea-stats'] });
      setResolveModalId(null);
      setResolveNotes('');
    },
  });

  // ── Physical count (inventory_adjustment) ─────────────────────────────
  const [countModalOpen, setCountModalOpen] = useState(false);
  const [countDrugName, setCountDrugName] = useState('');
  const [countLotNumber, setCountLotNumber] = useState('');
  const [countSchedule, setCountSchedule] = useState('II');
  const [countUnit, setCountUnit] = useState('mg');
  const [countAmount, setCountAmount] = useState('');
  const countMutation = useMutation({
    mutationFn: async () => {
      if (!org_id || !user?.id) throw new Error('Missing context');
      const amt = parseFloat(countAmount);
      if (isNaN(amt) || amt < 0) throw new Error('Invalid count');

      const { data: inserted, error } = await supabase
        .from('dea_controlled_substance_log')
        .insert({
          org_id,
          transaction_type: 'inventory_adjustment',
          transaction_date: new Date().toISOString(),
          drug_name: countDrugName,
          dea_schedule: countSchedule,
          lot_number: countLotNumber || null,
          quantity_remaining: amt,
          unit_of_measure: countUnit,
          provider_name: user.email ?? 'Unknown',
          created_by_id: user.id,
          created_by_name: user.email ?? 'Unknown',
          notes: 'Physical count reconciliation',
        })
        .select('log_id, running_balance')
        .single();
      if (error) throw error;

      // Audit emission for physical count — durable insert into dea_access_log
      emitDeaAudit({
        orgId: org_id,
        userId: user.id,
        userEmail: user.email ?? '',
        actionType: 'inventory_adjustment',
        drugName: countDrugName,
        schedule: countSchedule,
        quantityDispensed: amt,
        inventoryAfter: inserted.running_balance ?? null,
        deaLogId: inserted.log_id,
      });

      return inserted;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dea-discrepancies'] });
      queryClient.invalidateQueries({ queryKey: ['dea-inventory'] });
      queryClient.invalidateQueries({ queryKey: ['dea-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dea-log'] });
      setCountModalOpen(false);
      setCountDrugName('');
      setCountLotNumber('');
      setCountAmount('');
    },
  });

  // ── Witness verification from reconciliation ──────────────────────────
  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('provider_id, first_name, last_name')
        .order('last_name');
      if (error) throw error;
      return data;
    },
    enabled: tab === 'reconciliation',
  });

  const [verifyModalId, setVerifyModalId] = useState<string | null>(null);
  const [verifyWitnessId, setVerifyWitnessId] = useState('');
  const [verifyWitnessName, setVerifyWitnessName] = useState('');
  const verifyMutation = useMutation({
    mutationFn: async (logId: string) => {
      const { error } = await supabase
        .from('dea_controlled_substance_log')
        .update({
          witness_id: verifyWitnessId,
          witness_name: verifyWitnessName,
          witness_signature_timestamp: new Date().toISOString(),
          witness_verified: true,
        })
        .eq('log_id', logId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dea-unverified-waste'] });
      queryClient.invalidateQueries({ queryKey: ['dea-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dea-log'] });
      setVerifyModalId(null);
      setVerifyWitnessId('');
      setVerifyWitnessName('');
    },
  });

  // ── UI ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-full">
      {/* Header */}
      <header className="border-b border-pt-border/60">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-pt-text-muted hover:text-pt-gold transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <PtLogo size={32} />
            <div>
              <h1 className="text-base font-display font-semibold tracking-wide leading-tight">
                DEA Controlled <span className="text-pt-gold">Substance Log</span>
              </h1>
              <p className="text-xs text-pt-text-dim">21 CFR 1304.04 Compliant</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dea-log/new')}
              className="flex items-center gap-2 bg-pt-gold/20 text-pt-gold px-4 py-2 rounded border border-pt-gold/30 hover:bg-pt-gold/30 text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />
              New Entry
            </button>
            <button
              onClick={() => setReportModalOpen(true)}
              className="flex items-center gap-2 bg-pt-dark text-pt-text-muted px-3 py-2 rounded border border-pt-input-border hover:border-pt-gold/50 text-sm"
            >
              <Download className="w-4 h-4" />
              Reports
            </button>
            <span className="text-xs text-pt-text-muted hidden sm:inline">{user?.email}</span>
            <button
              onClick={signOut}
              className="text-xs uppercase tracking-wide text-pt-text-muted hover:text-pt-gold transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <button
            onClick={() => { setTab('reconciliation'); }}
            className="bg-pt-panel border border-pt-border rounded-lg p-4 text-left hover:border-pt-amber/40 transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-pt-amber" />
              <span className="text-xs text-pt-text-muted">Unverified Waste</span>
            </div>
            <div className="text-2xl font-bold text-pt-amber font-mono">{stats?.unverifiedWaste || 0}</div>
          </button>
          <button
            onClick={() => { setTab('reconciliation'); }}
            className="bg-pt-panel border border-pt-border rounded-lg p-4 text-left hover:border-pt-red/40 transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-pt-red" />
              <span className="text-xs text-pt-text-muted">Open Discrepancies</span>
            </div>
            <div className="text-2xl font-bold text-pt-red font-mono">{stats?.openDiscrepancies || 0}</div>
          </button>
          <div className="bg-pt-panel border border-pt-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-pt-red" />
              <span className="text-xs text-pt-text-muted">Schedule II (30d)</span>
            </div>
            <div className="text-2xl font-bold text-pt-red font-mono">{stats?.scheduleIITransactions || 0}</div>
          </div>
          <button
            onClick={() => setTab('inventory')}
            className="bg-pt-panel border border-pt-border rounded-lg p-4 text-left hover:border-pt-cyan/40 transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-pt-cyan" />
              <span className="text-xs text-pt-text-muted">Drugs in Stock</span>
            </div>
            <div className="text-2xl font-bold text-pt-cyan font-mono">{stats?.drugsInInventory || 0}</div>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto border-b border-pt-border pb-px">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-t transition-colors whitespace-nowrap ${
                tab === t.key
                  ? 'bg-pt-panel border border-pt-border border-b-pt-panel text-pt-gold font-semibold -mb-px'
                  : 'text-pt-text-muted hover:text-pt-text'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB: Ledger ──────────────────────────────────────────── */}
        {tab === 'ledger' && (
          <>
            {/* Filters */}
            <div className="bg-pt-panel border border-pt-border rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-3">
                <div className="col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pt-text-dim" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Drug, provider, or patient…"
                      className="w-full bg-pt-dark border border-pt-input-border rounded pl-10 pr-3 py-2 text-pt-text placeholder-pt-text-dim text-sm focus:outline-none focus:border-pt-gold"
                    />
                  </div>
                </div>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm"
                />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm"
                />
                <select
                  value={filterSchedule}
                  onChange={(e) => setFilterSchedule(e.target.value)}
                  className="bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm"
                >
                  <option value="all">All Schedules</option>
                  <option value="II">Schedule II</option>
                  <option value="III">Schedule III</option>
                  <option value="IV">Schedule IV</option>
                  <option value="V">Schedule V</option>
                </select>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="receipt">Receipt</option>
                  <option value="administration">Administration</option>
                  <option value="waste">Waste</option>
                  <option value="transfer_in">Transfer In</option>
                  <option value="transfer_out">Transfer Out</option>
                  <option value="inventory_adjustment">Adjustment</option>
                </select>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={showDiscrepanciesOnly}
                    onChange={(e) => setShowDiscrepanciesOnly(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-pt-input-border"
                  />
                  <span className="text-pt-text-muted">Discrepancies only</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={showUnverifiedOnly}
                    onChange={(e) => setShowUnverifiedOnly(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-pt-input-border"
                  />
                  <span className="text-pt-text-muted">Unverified waste only</span>
                </label>
              </div>
            </div>

            {/* Log Table */}
            <div className="bg-pt-panel border border-pt-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-pt-dark border-b border-pt-border">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Drug</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Sch</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Qty</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Balance</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Provider</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Patient</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Status</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-pt-border/50">
                    {ledgerLoading ? (
                      <tr><td colSpan={10} className="text-center py-8 text-pt-text-dim text-sm">Loading…</td></tr>
                    ) : logEntries?.length === 0 ? (
                      <tr><td colSpan={10} className="text-center py-8 text-pt-text-dim text-sm">No entries match your filters</td></tr>
                    ) : (
                      logEntries?.map((e) => (
                        <tr key={e.log_id} className="hover:bg-pt-dark/50 transition-colors">
                          <td className="px-4 py-3 text-sm">
                            <div>{format(new Date(e.transaction_date), 'MMM dd, yyyy')}</div>
                            <div className="text-xs text-pt-text-dim">{format(new Date(e.transaction_date), 'h:mm a')}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-pt-cyan/15 text-pt-cyan px-2 py-0.5 rounded">{TXN_LABELS[e.transaction_type] || e.transaction_type}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-sm">{e.drug_name}</div>
                            {e.drug_strength && <div className="text-xs text-pt-text-dim">{e.drug_strength}</div>}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-mono font-bold text-sm ${scheduleColor(e.dea_schedule)}`}>{e.dea_schedule}</span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-sm">
                            {e.quantity_received || e.quantity_administered || e.quantity_wasted || 0} {e.unit_of_measure}
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-sm text-pt-cyan">
                            {e.running_balance} {e.unit_of_measure}
                          </td>
                          <td className="px-4 py-3 text-sm">{e.provider_name}</td>
                          <td className="px-4 py-3 text-sm text-pt-text-muted">{e.patient_name || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col items-center gap-1">
                              {e.discrepancy_flag && (
                                <div className="flex items-center gap-1">
                                  {e.discrepancy_resolved
                                    ? <CheckCircle className="w-3.5 h-3.5 text-pt-green" />
                                    : <AlertTriangle className="w-3.5 h-3.5 text-pt-red" />}
                                  <span className="text-[10px] text-pt-text-muted">{e.discrepancy_resolved ? 'Resolved' : 'Open'}</span>
                                </div>
                              )}
                              {e.transaction_type === 'waste' && (
                                <div className="flex items-center gap-1">
                                  {e.witness_verified
                                    ? <UserCheck className="w-3.5 h-3.5 text-pt-green" />
                                    : <Clock className="w-3.5 h-3.5 text-pt-amber" />}
                                  <span className="text-[10px] text-pt-text-muted">{e.witness_verified ? 'Verified' : 'Pending'}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Link to={`/dea-log/${e.log_id}`} className="text-pt-cyan hover:text-pt-gold">
                              <FileText className="w-4 h-4" />
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── TAB: Inventory ──────────────────────────────────────── */}
        {tab === 'inventory' && (
          <div className="bg-pt-panel border border-pt-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-pt-dark border-b border-pt-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Drug</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Strength / Form</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Schedule</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Lot</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Expires</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Current Qty</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Txns</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Last Activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pt-border/50">
                  {invLoading ? (
                    <tr><td colSpan={8} className="text-center py-8 text-pt-text-dim text-sm">Loading…</td></tr>
                  ) : inventoryData?.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-pt-text-dim text-sm">No inventory data</td></tr>
                  ) : (
                    inventoryData?.map((r, i) => (
                      <tr key={i} className="hover:bg-pt-dark/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-sm">{r.drug_name}</td>
                        <td className="px-4 py-3 text-sm text-pt-text-muted">{r.drug_strength} {r.drug_form}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-mono font-bold ${scheduleColor(r.dea_schedule)}`}>{r.dea_schedule}</span>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">{r.lot_number || '—'}</td>
                        <td className="px-4 py-3 text-sm">
                          {r.expiration_date ? format(new Date(r.expiration_date), 'MMM dd, yyyy') : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold text-pt-cyan">
                          {r.current_quantity} {r.unit_of_measure}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-pt-text-muted">{r.transaction_count}</td>
                        <td className="px-4 py-3 text-sm text-pt-text-muted">
                          {format(new Date(r.last_transaction_date), 'MMM dd, yyyy')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB: Reconciliation ─────────────────────────────────── */}
        {tab === 'reconciliation' && (
          <div className="space-y-6">
            {/* Physical count button */}
            <div className="flex justify-end">
              <button
                onClick={() => setCountModalOpen(true)}
                className="flex items-center gap-2 bg-pt-gold/20 text-pt-gold px-4 py-2 rounded border border-pt-gold/30 hover:bg-pt-gold/30 text-sm font-semibold"
              >
                <ClipboardCheck className="w-4 h-4" />
                Record Physical Count
              </button>
            </div>

            {/* Open Discrepancies */}
            <div>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-pt-red" />
                Discrepancies
              </h2>
              <div className="bg-pt-panel border border-pt-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-pt-dark border-b border-pt-border">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Drug</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Sch</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Type</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Amount</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Notes</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Status</th>
                        <th className="w-24"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-pt-border/50">
                      {discLoading ? (
                        <tr><td colSpan={8} className="text-center py-6 text-pt-text-dim text-sm">Loading…</td></tr>
                      ) : discrepancies?.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-6 text-pt-green text-sm flex items-center justify-center gap-2">
                          <CheckCircle className="w-4 h-4" /> No discrepancies
                        </td></tr>
                      ) : (
                        discrepancies?.map((d) => (
                          <tr key={d.log_id} className="hover:bg-pt-dark/50 transition-colors">
                            <td className="px-4 py-3 text-sm">{format(new Date(d.transaction_date), 'MMM dd, yyyy')}</td>
                            <td className="px-4 py-3 text-sm font-medium">{d.drug_name}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-mono font-bold ${scheduleColor(d.dea_schedule)}`}>{d.dea_schedule}</span>
                            </td>
                            <td className="px-4 py-3 text-sm">{d.discrepancy_type}</td>
                            <td className="px-4 py-3 text-right font-mono text-sm">{d.discrepancy_amount} {d.unit_of_measure}</td>
                            <td className="px-4 py-3 text-sm text-pt-text-muted max-w-[200px] truncate">{d.discrepancy_notes || '—'}</td>
                            <td className="px-4 py-3 text-center">
                              {d.discrepancy_resolved ? (
                                <span className="text-xs bg-pt-green/15 text-pt-green px-2 py-0.5 rounded">Resolved</span>
                              ) : (
                                <span className="text-xs bg-pt-red/15 text-pt-red px-2 py-0.5 rounded">Open</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {!d.discrepancy_resolved && (
                                <button
                                  onClick={() => setResolveModalId(d.log_id)}
                                  className="text-xs bg-pt-green/15 text-pt-green px-3 py-1 rounded border border-pt-green/30 hover:bg-pt-green/25"
                                >
                                  Resolve
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Unverified Waste */}
            <div>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-pt-amber" />
                Unverified Waste
              </h2>
              <div className="bg-pt-panel border border-pt-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-pt-dark border-b border-pt-border">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Drug</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Sch</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Wasted</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Provider</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Patient</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Pending</th>
                        <th className="w-24"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-pt-border/50">
                      {uvLoading ? (
                        <tr><td colSpan={8} className="text-center py-6 text-pt-text-dim text-sm">Loading…</td></tr>
                      ) : unverifiedWaste?.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-6 text-pt-green text-sm">
                          <span className="flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4" /> All waste verified</span>
                        </td></tr>
                      ) : (
                        unverifiedWaste?.map((w) => (
                          <tr key={w.log_id} className="hover:bg-pt-dark/50 transition-colors">
                            <td className="px-4 py-3 text-sm">{format(new Date(w.transaction_date), 'MMM dd, yyyy')}</td>
                            <td className="px-4 py-3 text-sm font-medium">{w.drug_name}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-mono font-bold ${scheduleColor(w.dea_schedule)}`}>{w.dea_schedule}</span>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-sm">{w.quantity_wasted} {w.unit_of_measure}</td>
                            <td className="px-4 py-3 text-sm">{w.provider_name}</td>
                            <td className="px-4 py-3 text-sm text-pt-text-muted">{w.patient_name || '—'}</td>
                            <td className="px-4 py-3 text-right font-mono text-sm text-pt-amber">{Math.round(w.hours_pending)}h</td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => setVerifyModalId(w.log_id)}
                                className="text-xs bg-pt-amber/15 text-pt-amber px-3 py-1 rounded border border-pt-amber/30 hover:bg-pt-amber/25"
                              >
                                Verify
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Form 222 ───────────────────────────────────────── */}
        {tab === 'form222' && (
          <div className="bg-pt-panel border border-pt-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-pt-dark border-b border-pt-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Drug</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Strength</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">NDC</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Lot</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Qty</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Form 222 #</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Supplier</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Received By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pt-border/50">
                  {f222Loading ? (
                    <tr><td colSpan={9} className="text-center py-8 text-pt-text-dim text-sm">Loading…</td></tr>
                  ) : form222Data?.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-pt-text-dim text-sm">No Form 222 receipts</td></tr>
                  ) : (
                    form222Data?.map((r) => (
                      <tr key={r.log_id} className="hover:bg-pt-dark/50 transition-colors">
                        <td className="px-4 py-3 text-sm">{format(new Date(r.transaction_date), 'MMM dd, yyyy')}</td>
                        <td className="px-4 py-3 text-sm font-medium">{r.drug_name}</td>
                        <td className="px-4 py-3 text-sm">{r.drug_strength}</td>
                        <td className="px-4 py-3 text-sm font-mono">{r.ndc_code || '—'}</td>
                        <td className="px-4 py-3 text-sm font-mono">{r.lot_number || '—'}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{r.quantity_received} {r.unit_of_measure}</td>
                        <td className="px-4 py-3 text-sm font-mono font-semibold text-pt-gold">{r.form_222_number || '—'}</td>
                        <td className="px-4 py-3 text-sm">{r.supplier_name || '—'}</td>
                        <td className="px-4 py-3 text-sm">{r.received_by_name}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB: Provider Activity ──────────────────────────────── */}
        {tab === 'provider' && (
          <div className="bg-pt-panel border border-pt-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-pt-dark border-b border-pt-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Provider</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">DEA #</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Drug</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Sch</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Qty</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Patient</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Witness</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pt-border/50">
                  {provLoading ? (
                    <tr><td colSpan={9} className="text-center py-8 text-pt-text-dim text-sm">Loading…</td></tr>
                  ) : providerData?.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-pt-text-dim text-sm">No provider activity in the last 30 days</td></tr>
                  ) : (
                    providerData?.map((p, i) => (
                      <tr key={i} className="hover:bg-pt-dark/50 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium">{p.provider_name}</td>
                        <td className="px-4 py-3 text-sm font-mono text-pt-text-muted">{p.provider_dea_number || '—'}</td>
                        <td className="px-4 py-3 text-sm">{format(new Date(p.transaction_date), 'MMM dd')}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-pt-cyan/15 text-pt-cyan px-2 py-0.5 rounded">{TXN_LABELS[p.transaction_type] || p.transaction_type}</span>
                        </td>
                        <td className="px-4 py-3 text-sm">{p.drug_name}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-mono font-bold ${scheduleColor(p.dea_schedule)}`}>{p.dea_schedule}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{p.quantity} {p.unit_of_measure}</td>
                        <td className="px-4 py-3 text-sm text-pt-text-muted">{p.patient_name || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          {p.witness_verified ? <UserCheck className="w-4 h-4 text-pt-green mx-auto" /> : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB: Daily Usage ────────────────────────────────────── */}
        {tab === 'daily' && (
          <div className="bg-pt-panel border border-pt-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-pt-dark border-b border-pt-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Drug</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Schedule</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Administered</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide">Wasted</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide"># Admin</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide"># Waste</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-pt-text-muted uppercase tracking-wide"># Discrepancies</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pt-border/50">
                  {dailyLoading ? (
                    <tr><td colSpan={8} className="text-center py-8 text-pt-text-dim text-sm">Loading…</td></tr>
                  ) : dailyData?.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-pt-text-dim text-sm">No usage data in the last 30 days</td></tr>
                  ) : (
                    dailyData?.map((d, i) => (
                      <tr key={i} className="hover:bg-pt-dark/50 transition-colors">
                        <td className="px-4 py-3 text-sm">{format(new Date(d.transaction_date), 'MMM dd, yyyy')}</td>
                        <td className="px-4 py-3 text-sm font-medium">{d.drug_name}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-mono font-bold ${scheduleColor(d.dea_schedule)}`}>{d.dea_schedule}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{d.total_administered}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{d.total_wasted}</td>
                        <td className="px-4 py-3 text-center font-mono text-sm text-pt-text-muted">{d.administration_count}</td>
                        <td className="px-4 py-3 text-center font-mono text-sm text-pt-text-muted">{d.waste_count}</td>
                        <td className="px-4 py-3 text-center">
                          {d.discrepancy_count > 0 ? (
                            <span className="font-mono text-sm text-pt-red font-bold">{d.discrepancy_count}</span>
                          ) : (
                            <span className="font-mono text-sm text-pt-text-dim">0</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* ── Modals ───────────────────────────────────────────────────── */}

      {/* Resolve Discrepancy Modal */}
      {resolveModalId && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-pt-panel border border-pt-border rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4">Resolve Discrepancy</h2>
            <textarea
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              rows={4}
              placeholder="Resolution notes…"
              className="w-full bg-pt-dark border border-pt-input-border rounded p-3 text-pt-text text-sm mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => resolveMutation.mutate(resolveModalId)}
                disabled={!resolveNotes.trim() || resolveMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 bg-pt-green/20 text-pt-green px-4 py-2 rounded border border-pt-green/30 hover:bg-pt-green/30 text-sm disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                {resolveMutation.isPending ? 'Resolving…' : 'Resolve'}
              </button>
              <button
                onClick={() => { setResolveModalId(null); setResolveNotes(''); }}
                className="flex-1 text-pt-text-muted bg-pt-dark border border-pt-input-border px-4 py-2 rounded text-sm hover:border-pt-gold/50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verify Witness Modal */}
      {verifyModalId && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-pt-panel border border-pt-border rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-pt-amber" />
              Verify Waste Witness
            </h2>
            <div className="mb-4">
              <label className="block text-sm text-pt-text-muted mb-1">Select Witness</label>
              <select
                value={verifyWitnessId}
                onChange={(e) => {
                  setVerifyWitnessId(e.target.value);
                  const p = providers?.find((pr) => pr.provider_id === e.target.value);
                  if (p) setVerifyWitnessName(`${p.first_name} ${p.last_name}`);
                }}
                className="w-full bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm"
              >
                <option value="">Select provider…</option>
                {providers?.map((p) => (
                  <option key={p.provider_id} value={p.provider_id}>
                    {p.first_name} {p.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => verifyMutation.mutate(verifyModalId)}
                disabled={!verifyWitnessId || verifyMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 bg-pt-green/20 text-pt-green px-4 py-2 rounded border border-pt-green/30 hover:bg-pt-green/30 text-sm disabled:opacity-50"
              >
                <UserCheck className="w-4 h-4" />
                {verifyMutation.isPending ? 'Verifying…' : 'Verify'}
              </button>
              <button
                onClick={() => { setVerifyModalId(null); setVerifyWitnessId(''); setVerifyWitnessName(''); }}
                className="flex-1 text-pt-text-muted bg-pt-dark border border-pt-input-border px-4 py-2 rounded text-sm hover:border-pt-gold/50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Physical Count Modal */}
      {countModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-pt-panel border border-pt-border rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-pt-gold" />
              Physical Count
            </h2>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm text-pt-text-muted mb-1">Drug Name</label>
                <input
                  value={countDrugName}
                  onChange={(e) => setCountDrugName(e.target.value)}
                  className="w-full bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-pt-text-muted mb-1">Lot Number</label>
                <input
                  value={countLotNumber}
                  onChange={(e) => setCountLotNumber(e.target.value)}
                  className="w-full bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-pt-text-muted mb-1">Schedule</label>
                  <select
                    value={countSchedule}
                    onChange={(e) => setCountSchedule(e.target.value)}
                    className="w-full bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm"
                  >
                    {['I','II','III','IV','V'].map((s) => <option key={s} value={s}>Schedule {s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-pt-text-muted mb-1">Unit</label>
                  <select
                    value={countUnit}
                    onChange={(e) => setCountUnit(e.target.value)}
                    className="w-full bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm"
                  >
                    {['mg','mL','mcg','units','tablets','patches','vials','ampules'].map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-pt-text-muted mb-1">Counted Amount</label>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={countAmount}
                  onChange={(e) => setCountAmount(e.target.value)}
                  className="w-full bg-pt-dark border border-pt-input-border rounded px-3 py-2 text-pt-text text-sm font-mono"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => countMutation.mutate()}
                disabled={!countDrugName.trim() || !countAmount || countMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 bg-pt-gold/20 text-pt-gold px-4 py-2 rounded border border-pt-gold/30 hover:bg-pt-gold/30 text-sm disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {countMutation.isPending ? 'Saving…' : 'Post Count'}
              </button>
              <button
                onClick={() => setCountModalOpen(false)}
                className="flex-1 text-pt-text-muted bg-pt-dark border border-pt-input-border px-4 py-2 rounded text-sm hover:border-pt-gold/50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {reportModalOpen && <DEAReportModal onClose={() => setReportModalOpen(false)} />}
    </div>
  );
}
